import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/invoice_sending_options';

const UpdateInvoiceSendingOptionsVariablesSchema = z.object({
  generateInvoiceInDebtorLocale: z.boolean().optional(),
  defaultInvoiceTemplateId: z.string().nullable().optional(),
  defaultCreditNoteTemplateId: z.string().nullable().optional(),
  displayQRCode: z.boolean().optional(),
});

export type UpdateInvoiceSendingOptionsVariables = z.infer<
  typeof UpdateInvoiceSendingOptionsVariablesSchema
>;

export async function updateInvoiceSendingOptionsFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateInvoiceSendingOptionsVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdateInvoiceSendingOptionsVariablesSchema.parse(variables));
}

export function useUpdateInvoiceSendingOptions() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateInvoiceSendingOptionsVariables>({
    mutationFn: variables => updateInvoiceSendingOptionsFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['invoiceSendingOptions'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateInvoiceSendingOptionsVariables,
    'updateInvoiceSendingOptions'
  >('updateInvoiceSendingOptions', mutation);
}
